import { Profile } from '../../api/types/profile';
import { PreprocessedProfileField, UserProfileFieldTypes } from '../../components/UserProfileField/types';
import _ from 'lodash';

export const preprocessUserProfileForLookupPage = (profile: Profile) => {
  const {
    firstName,
    lastName,
    freePurchases,
    firstTrial,
    subscriptionId,
    subscriptionExpirationDate,
    subscriptionIntervals,
    subscriptionTransaction,
    isAdmin,
    isTester,
    createdAt,
    updatedAt,
    lastSeenAt,
    lessonPlaybackStates,
    storyPlaybackStates,
    stats,
    aluxExpFinishedLessons,
    aluxExpOpenedLessons,
    likedLessons,
    likedStories,
    lessonFavorites,
    storyFavorites,
    gifts,
    currentSurveys,
    currentLearningPaths,
    initialSurvey,
    learningPaths,
    ...otherProfileProps
  } = profile;

  const readOnlyProps = new Array<PreprocessedProfileField>();
  readOnlyProps.push({
    displayName: 'Full name',
    fieldType: UserProfileFieldTypes.text,
    data: `${firstName} ${lastName}`,
  });
  readOnlyProps.push({
    displayName: 'Special tags',
    fieldType: UserProfileFieldTypes.custom,
    data: { isAdmin: isAdmin || false, isTester: isTester || false },
    extraData: 'specialTags',
  });
  !!gifts &&
    gifts?.length > 0 &&
    readOnlyProps.push({
      displayName: 'Gifts',
      fieldType: UserProfileFieldTypes.table,
      data: gifts,
      extraData: 'gifts',
    });
  !!freePurchases &&
    readOnlyProps.push({
      displayName: 'Free purchases (Deprecated)',
      fieldType: UserProfileFieldTypes.list,
      data: freePurchases,
      extraData: 'Purchase ID',
    });
  !!firstTrial &&
    readOnlyProps.push({
      displayName: 'Trial period',
      fieldType: UserProfileFieldTypes.custom,
      data: firstTrial,
      extraData: 'firstTrial',
    });
  !!subscriptionId &&
    readOnlyProps.push({
      displayName: 'Last subscription',
      fieldType: UserProfileFieldTypes.custom,
      data: {
        subscriptionId,
        subscriptionTransaction,
        subscriptionExpirationDate,
        subscriptionIntervals,
      },
      extraData: 'subscription',
    });

  !!currentSurveys &&
    currentSurveys?.length > 0 &&
    readOnlyProps.push({
      displayName: 'Current Surveys',
      fieldType: UserProfileFieldTypes.list,
      data: currentSurveys,
      extraData: 'Survey ID',
    });
  !!currentLearningPaths &&
    currentLearningPaths?.length > 0 &&
    readOnlyProps.push({
      displayName: 'Current Learning Paths',
      fieldType: UserProfileFieldTypes.list,
      data: currentLearningPaths,
      extraData: 'Learning Path ID',
    });
  initialSurvey?.length &&
    readOnlyProps.push({
      displayName: 'Initial Survey',
      fieldType: UserProfileFieldTypes.text,
      data: initialSurvey,
    });
  !!learningPaths &&
    learningPaths?.length > 0 &&
    readOnlyProps.push({
      displayName: 'Learning paths',
      fieldType: UserProfileFieldTypes.list,
      data: learningPaths,
      extraData: 'Learning Path ID',
    });

  let sessionPlaybackStates = lessonPlaybackStates?.length > 0 ? lessonPlaybackStates : [];
  if (storyPlaybackStates?.length > 0) {
    sessionPlaybackStates = sessionPlaybackStates.concat(storyPlaybackStates);
  }
  readOnlyProps.push({
    displayName: 'Playback history',
    fieldType: UserProfileFieldTypes.table,
    data: sessionPlaybackStates,
    extraData: 'playbackHistory',
  });
  let likedSessions = likedLessons?.length > 0 ? likedLessons : [];
  if (likedStories?.length > 0) {
    likedSessions = likedSessions.concat(likedStories);
  }
  likedSessions.length > 0 &&
    readOnlyProps.push({
      displayName: 'Content rating',
      fieldType: UserProfileFieldTypes.table,
      data: likedSessions,
      extraData: 'contentRating',
    });
  let sessionFavorites = lessonFavorites?.length > 0 ? lessonFavorites : [];
  if (storyFavorites?.length > 0) {
    sessionFavorites = sessionFavorites.concat(storyFavorites);
  }
  sessionFavorites.length > 0 &&
    readOnlyProps.push({
      displayName: 'Favorite content',
      fieldType: UserProfileFieldTypes.table,
      data: sessionFavorites,
      extraData: 'favoriteContent',
    });
  !!createdAt &&
    readOnlyProps.push({
      displayName: 'User lifetime',
      fieldType: UserProfileFieldTypes.custom,
      data: { createdAt, updatedAt, lastSeenAt },
      extraData: 'userLifetime',
    });
  !!stats &&
    readOnlyProps.push({
      displayName: 'User journey stats',
      fieldType: UserProfileFieldTypes.custom,
      data: stats,
      extraData: 'stats',
    });
  readOnlyProps.push({
    displayName: 'First Alux Experience progress (Deprecated)',
    fieldType: UserProfileFieldTypes.custom,
    data: { aluxExpOpenedLessons: aluxExpOpenedLessons, aluxExpFinishedLessons: aluxExpFinishedLessons },
    extraData: 'fae',
  });

  const otherFields = new Array<PreprocessedProfileField>();
  Object.entries(otherProfileProps).forEach((entry) => {
    const [fieldKey, fieldVal] = entry;
    otherFields.push({
      displayName: fieldKey,
      fieldType: ['string', 'number'].includes(typeof fieldVal)
        ? UserProfileFieldTypes.text
        : UserProfileFieldTypes.other,
      data: fieldVal,
    } as PreprocessedProfileField);
  });
  _.sortBy(otherFields, (item) => item?.displayName).forEach((entry) => {
    readOnlyProps.push(entry);
  });

  const editableProps = new Array<PreprocessedProfileField>();

  return { readOnly: readOnlyProps, editable: editableProps };
};
